import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaCheck, FaChevronRight } from "react-icons/fa";
import { fetchServiceDetail, fetchServicesPage } from "../api/servicesPage";
import { useHomePage } from "../context/HomePageContext";

const ProgressBar = ({ title, value }) => (
  <div>
    <div className="mb-2 flex items-center justify-between gap-3">
      <p className="text-[15px] font-semibold capitalize text-[#333333]">{title}</p>
      <span className="text-[14px] font-semibold text-brand-purple">{value}%</span>
    </div>
    <div className="relative h-[6px] rounded-full bg-[#EDE7F3]">
      <div
        className="absolute left-0 top-0 h-full rounded-full bg-brand-purple"
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
      />
      <span
        className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-[3px] border-brand-purple bg-white"
        style={{ left: `calc(${Math.min(Math.max(value, 0), 100)}% - 8px)` }}
      />
    </div>
  </div>
);

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div className="overflow-hidden rounded-[10px]">
    <button
      type="button"
      onClick={onToggle}
      className={`flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-[15px] font-semibold transition-colors ${
        isOpen ? "bg-brand-orange text-white" : "bg-brand-purple text-white"
      }`}
    >
      <span>{faq.question}</span>
      <span className="text-xl leading-none">{isOpen ? "−" : "+"}</span>
    </button>
    {isOpen && (
      <div className="bg-[#F5F0FA] px-5 py-4 text-[14px] leading-[1.75] text-[#555555]">
        {faq.answer}
      </div>
    )}
  </div>
);

const ServiceDetailPage = () => {
  const { id } = useParams();
  const { setContact } = useHomePage();
  const [service, setService] = useState(null);
  const [sidebarServices, setSidebarServices] = useState([]);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadDetail = async () => {
      setError(null);
      setService(null);
      setOpenFaqId(null);

      try {
        const [detail, list] = await Promise.all([
          fetchServiceDetail(id),
          fetchServicesPage(),
        ]);

        if (!isMounted) return;

        setService(detail.service);
        setSidebarServices(list.services);
        if (detail.contact) setContact(detail.contact);
        if (detail.service?.faqs?.[0]) {
          setOpenFaqId(detail.service.faqs[0].id);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Unable to load service details.");
        }
      }
    };

    loadDetail();

    return () => {
      isMounted = false;
    };
  }, [id, setContact]);

  if (error) {
    return (
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 text-center md:px-8">
          <p className="text-brand-purple">{error}</p>
          <Link to="/services" className="mt-4 inline-block text-brand-orange underline">
            Back to Services
          </Link>
        </div>
      </section>
    );
  }

  if (!service) {
    return (
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 text-center md:px-8">
          <p className="text-[#777777]">Loading service details...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white pb-16 pt-10 md:pb-20 md:pt-12 lg:pb-24">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 md:px-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-12 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {service.image && (
            <img
              src={service.image}
              alt={service.title}
              className="h-auto w-full rounded-[16px] object-cover"
            />
          )}

          <h1 className="mt-8 text-2xl font-bold leading-tight text-brand-purple md:text-3xl lg:text-[2rem]">
            {service.title}
          </h1>

          <div
            className="mt-5 text-[15px] leading-[1.85] text-[#555555] [&_p]:mb-4 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: service.description || "" }}
          />

          <div className="mt-10 grid gap-10 md:grid-cols-2 md:gap-12">
            <ul className="space-y-4">
              {service.descriptionPoints.map((point) => (
                <li key={point.id} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-purple text-white">
                    <FaCheck className="text-[11px]" aria-hidden="true" />
                  </span>
                  <span className="text-[15px] leading-[1.6] text-[#444444]">
                    {point.description}
                  </span>
                </li>
              ))}
            </ul>

            <div className="space-y-6">
              {service.servicePoints.map((point) => (
                <ProgressBar key={point.id} title={point.title} value={point.value} />
              ))}
            </div>
          </div>

          {service.faqs.length > 0 && (
            <div className="mt-14">
              <h2 className="text-2xl font-bold text-brand-purple md:text-[1.75rem]">
                Frequently Asked Questions
              </h2>
              <p className="mt-3 max-w-2xl text-[14px] leading-[1.75] text-[#666666]">
                Find answers to common questions about this treatment and your care journey.
              </p>

              <div className="mt-8 space-y-4">
                {service.faqs.map((faq) => (
                  <FaqItem
                    key={faq.id}
                    faq={faq}
                    isOpen={openFaqId === faq.id}
                    onToggle={() =>
                      setOpenFaqId((prev) => (prev === faq.id ? null : faq.id))
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="sticky top-28 overflow-hidden rounded-[16px] bg-white shadow-[0_12px_40px_rgba(118,9,130,0.12)]">
            <div className="space-y-3 p-3">
              {sidebarServices.map((item) => {
                const isActive = String(item.id) === String(id);

                return (
                  <Link
                    key={item.id}
                    to={`/services/${item.id}`}
                    className={`flex items-center justify-between gap-3 rounded-[10px] px-4 py-3.5 text-[15px] font-semibold transition-colors ${
                      isActive
                        ? "bg-brand-orange text-white"
                        : "bg-brand-purple text-white hover:bg-brand-purple-dark"
                    }`}
                  >
                    <span>{item.title}</span>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-md ${
                        isActive ? "bg-white text-brand-orange" : "bg-white text-brand-purple"
                      }`}
                    >
                      <FaChevronRight className="text-[11px]" aria-hidden="true" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default ServiceDetailPage;
